import { MAX_ATTACHMENTS, MAX_UPLOAD_BYTES, type Attachment, type Channel, type Me, type Message, type ReplyPreview } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowDown, Check, ChevronLeft, Code, MessagesSquare, File as FileIcon, Hash, Paperclip, Phone, Upload, Lock, MessageSquare, MoreHorizontal, Pencil, PictureInPicture2, Pin, Rss, SendHorizontal, Users, X, LogOut, Bell, BellOff } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Virtuoso, type Components, type VirtuosoHandle } from 'react-virtuoso';
import { Avatar, presenceText } from '../components/Avatar';
import { CallStage, Notice } from '../components/Call';
import { CallBar } from '../components/CallBar';
import { ChannelIcon } from '../components/ChannelIcon';
import { MessageRow } from '../components/MessageItem';
import { PeopleDialog } from '../components/PeopleDialog';
import { ArchiveDialog, EditRoomDialog, LeaveDialog, Modal } from '../components/RoomDialogs';
import { JiraFeedDialog } from '../components/JiraFeedDialog';
import { useJiraCreate } from '../components/CreateJiraIssueDialog';
import { parseJiraCommand } from '@novachat/client';
import { useJira } from '../components/JiraSettings';
import { firstName } from '../lib/avatar';
import { useDirectory } from '../lib/directory';
import { mentionQuery, plainText, toWire } from '../lib/mentions';
import { useComposer } from '../state/composer';
import { sizeText, uploadFile } from '../lib/upload';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useHasKeyboard, useMediaQuery } from '../lib/media';
import { clock } from '../lib/time';
import { closeThread, deleteMessage, followThread, loadLatest, loadOlder, loadOlderInThread, loadThread, markChannelRead, markThreadRead, notifyTyping, pinMessage, sendMessage, useChat, type PendingMessage } from '../state/chat';
import { closeThreadPanel, openThread, useThreadPanel } from '../state/thread';
import { goBack } from '../state/nav';
import { useLive } from '../state/live';
import { canPopOut, usePopout } from '../state/popout';
import { useVoice } from '../state/voice';
import { useDismiss } from '../lib/dismiss';

type Row = { message: Message; pending?: PendingMessage };

const drafts = new Map<string, string>();
const titleOf = (channel: Channel) => (channel.kind === 'dm' ? (channel.peer?.name ?? 'Direkt mesaj') : channel.kind === 'jira' ? 'Jira' : channel.name);

interface ChatToggle {
  /** Wide screens: the side panel is open. Narrow ones: the chat replaces the call. */
  open: boolean;
  wide: boolean;
  unread: boolean;
  onClick: () => void;
}

/**
 * A channel: its chat, and its call once you join. Every channel has both. In a call, wide
 * screens put the chat next to the call; narrower ones switch between the two.
 */
export function ChannelPage({ channel, me }: { channel: Channel; me: Me }) {
  const connected = useVoice((s) => s.channelId === channel.id && s.room !== null && s.connection !== 'connecting');
  const poppedOut = usePopout((s) => s.win !== null);
  // Popped out, the call lives in its own window and this page is all chat.
  const inCall = connected && !poppedOut;
  const wide = useMediaQuery('(min-width: 1024px)');
  const [panelOpen, setPanelOpen] = useState(true);
  const [chatInstead, setChatInstead] = useState(false);
  const unread = useChat((s) => s.reads[channel.id]?.unread ?? 0);
  // An open thread in this channel: next to the chat on wide screens, instead of it on narrow ones.
  const openRoot = useThreadPanel((s) => (s.open?.channelId === channel.id ? s.open.rootId : null));

  // Joining shows the call first on narrow screens.
  useEffect(() => {
    if (inCall) setChatInstead(false);
  }, [inCall]);

  const chatOpen = wide ? panelOpen : chatInstead;
  const toggle: ChatToggle | null = inCall
    ? { open: chatOpen, wide, unread: !chatOpen && unread > 0, onClick: () => (wide ? setPanelOpen((o) => !o) : setChatInstead((o) => !o)) }
    : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ChannelHeader channel={channel} me={me} chatToggle={toggle} canPop={inCall && canPopOut} />
      {/* While the call is not on screen: who is in it, and a way in (or your mic and the way out). */}
      {!inCall && <CallBar channel={channel} me={me} />}
      {inCall && !wide && chatInstead && <CallBar channel={channel} me={me} onShowCall={() => setChatInstead(false)} />}
      <Notice />
      {connected && poppedOut && (
        <div role="status" className="mx-3 mt-3 flex items-center gap-3 rounded-md border border-line-strong bg-surface-2 px-3 py-2 text-[13px] text-fg-2 md:mx-5">
          <PictureInPicture2 size={16} className="flex-none text-green-400" aria-hidden="true" />
          <span className="min-w-0 flex-1">Görüşme ayrı pencerede.</span>
          <button type="button" onClick={() => usePopout.getState().close()} className="nc-press h-8 rounded-sm border border-line-strong bg-surface-3 px-3 text-[13px] font-semibold text-fg-1">
            Buraya al
          </button>
        </div>
      )}
      {inCall ? (
        <div className="flex min-h-0 flex-1">
          {(wide || !chatInstead) && (
            <div className="flex min-w-0 flex-1 flex-col">
              <CallStage />
            </div>
          )}
          {chatOpen && (
            <aside aria-label="Sohbet" className={`flex min-h-0 flex-col ${wide ? 'w-[360px] flex-none border-l border-line xl:w-[400px]' : 'min-w-0 flex-1'}`}>
              {openRoot ? <ThreadPanel channel={channel} me={me} rootId={openRoot} /> : <Chat channel={channel} me={me} />}
            </aside>
          )}
        </div>
      ) : openRoot && !wide ? (
        <ThreadPanel channel={channel} me={me} rootId={openRoot} />
      ) : openRoot ? (
        <div className="flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 flex-col">
            <Chat channel={channel} me={me} />
          </div>
          <aside className="flex w-[380px] flex-none flex-col border-l border-line xl:w-[420px]">
            <ThreadPanel channel={channel} me={me} rootId={openRoot} />
          </aside>
        </div>
      ) : (
        <Chat channel={channel} me={me} />
      )}
    </div>
  );
}

/** Starts or joins this channel's call: a plain phone icon like the others (the bar below says who is inside). */
function CallButton({ channel, me }: { channel: Channel; me: Me }) {
  const here = useVoice((s) => s.channelId === channel.id);
  const connecting = useVoice((s) => s.channelId === channel.id && s.connection === 'connecting');
  const inside = useLive((s) => s.voice[channel.id]);
  const count = inside?.length ?? 0;
  if ((here && !connecting) || !channel.permissions.includes('voice_join')) return null;
  const elsewhere = !here && (inside ?? []).some((p) => p.userId === me.id);
  const live = count > 0;
  const label = connecting ? 'Bağlanıyor…' : elsewhere ? 'Görüşmeyi bu cihaza al' : live ? `Görüşmeye katıl, ${count} kişi` : 'Görüşme başlat';
  return (
    <button
      type="button"
      disabled={connecting}
      onClick={() => void useVoice.getState().join(channel.id)}
      aria-label={label}
      title={elsewhere ? 'Başka bir cihazda bu görüşmedesin; buraya alınca oradaki bağlantı kapanır.' : `${label} (mikrofonun kapalı katılırsın)`}
      className={`nc-press grid size-[34px] flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2 hover:text-fg-1 disabled:opacity-60 ${connecting ? 'animate-pulse' : ''}`}
    >
      <Phone size={18} aria-hidden="true" />
    </button>
  );
}

/**
 * History, live updates and the composer (design D, "Metin"). With `threadRootId`, the same for
 * that thread: its replies under its first message (docs/plans/2026-11-04-threads-design.md).
 */
function Chat({ channel, me, threadRootId }: { channel: Channel; me: Me; threadRootId?: string }) {
  const log = useChat((s) => (threadRootId ? s.threads[threadRootId] : s.logs[channel.id]));
  const root = useChat((s) => (threadRootId ? s.threads[threadRootId]?.root : undefined));
  const loadNewest = useCallback(() => (threadRootId ? loadThread(threadRootId) : loadLatest(channel.id)), [threadRootId, channel.id]);
  const loadEarlier = useCallback(() => void (threadRootId ? loadOlderInThread(threadRootId) : loadOlder(channel.id)), [threadRootId, channel.id]);
  const outbox = useChat((s) => s.outbox);
  const [error, setError] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<ReplyPreview | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Message | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const list = useRef<VirtuosoHandle>(null);
  const composer = useRef<HTMLTextAreaElement>(null);
  const atBottomRef = useRef(true);
  atBottomRef.current = atBottom;
  // Until the reader scrolls, growth (late fonts, link cards, pictures) keeps the newest message in view
  // even when it jumps more than the "at bottom" margin at once.
  const readerScrolled = useRef(false);
  const stayDown = () => atBottomRef.current || !readerScrolled.current;
  const addFiles = useRef<(files: File[]) => void>(() => {});
  // What the reader's roles here allow (docs/plans/2026-11-03-channel-roles-design.md).
  const canWrite = channel.permissions.includes('write');
  const canAttach = canWrite && channel.permissions.includes('files');
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const hasFiles = (event: React.DragEvent) => event.dataTransfer.types.includes('Files');
  const toEnd = useCallback(() => list.current?.scrollTo({ top: Number.MAX_SAFE_INTEGER }), []);
  const viewport = useRef<HTMLDivElement>(null);

  // The list gets shorter when the reply bar, a longer draft or a phone keyboard appears; keep the newest message in view.
  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) toEnd();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [toEnd]);

  // Where the user had stopped reading when the channel opened: the "Yeni mesajlar" line goes there.
  const [opened] = useState(() => {
    if (threadRootId) {
      const followed = useChat.getState().followed[threadRootId];
      return { lastRead: followed?.lastReadMessageId ?? null, hadUnread: (followed?.unread ?? 0) > 0 };
    }
    const read = useChat.getState().reads[channel.id];
    return { lastRead: read?.lastReadMessageId ?? null, hadUnread: (read?.unread ?? 0) > 0 };
  });

  useEffect(() => {
    if (log) return;
    loadNewest().catch((e: unknown) => setError(errorMessage(e)));
  }, [loadNewest, log]);
  // A thread closed on screen stops being kept up to date.
  useEffect(() => (threadRootId ? () => closeThread(threadRootId) : undefined), [threadRootId]);

  const lastId = log?.messages.at(-1)?.id;
  useEffect(() => {
    const mark = () => {
      if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
      if (threadRootId) markThreadRead(threadRootId);
      else markChannelRead(channel.id);
    };
    mark();
    window.addEventListener('focus', mark);
    document.addEventListener('visibilitychange', mark);
    return () => {
      window.removeEventListener('focus', mark);
      document.removeEventListener('visibilitychange', mark);
    };
  }, [channel.id, threadRootId, lastId]);

  const rows = useMemo<Row[]>(() => {
    const confirmed = (log?.messages ?? []).map((message) => ({ message }));
    const waiting = outbox
      .filter((p) => p.channelId === channel.id && (threadRootId ? p.threadRootId === threadRootId : !p.threadRootId))
      .map((pending) => ({
        pending,
        message: {
          id: '',
          channelId: channel.id,
          author: { id: me.id, name: me.name },
          content: pending.content,
          clientId: pending.clientId,
          kind: 'user',
          replyTo: pending.replyTo,
          reactions: [],
          pinned: false,
          mentions: { userIds: [], everyone: false },
          attachments: pending.attachments ?? [],
          links: [],
          createdAt: pending.createdAt,
          editedAt: null,
          deletedAt: null,
          threadRootId: threadRootId ?? null,
        } satisfies Message,
      }));
    return [...confirmed, ...waiting];
  }, [log?.messages, outbox, channel.id, threadRootId, me.id, me.name]);

  const firstUnreadId = useMemo(() => {
    if (!opened.hadUnread) return null;
    return log?.messages.find((m) => m.kind !== 'system' && m.author?.id !== me.id && (!opened.lastRead || m.id > opened.lastRead))?.id ?? null;
  }, [log?.messages, opened, me.id]);

  const jumpTo = useCallback(
    (messageId: string) => {
      const index = log?.messages.findIndex((m) => m.id === messageId) ?? -1;
      if (index < 0 || !log) return;
      // Leaving the bottom on purpose: don't let the "stay on the newest message" pin pull us back.
      atBottomRef.current = false;
      readerScrolled.current = true;
      list.current?.scrollToIndex({ index: log.firstIndex + index, align: 'center', behavior: 'smooth' });
      setHighlightId(messageId);
      setTimeout(() => setHighlightId((id) => (id === messageId ? null : id)), 1600);
    },
    [log],
  );

  // Opened from search: load older pages until the message is there (at most 20), then show it.
  const jumpTarget = useChat((s) => (s.jump?.channelId === channel.id && !threadRootId ? s.jump.messageId : null));
  const jumpPages = useRef(0);
  useEffect(() => {
    if (!jumpTarget || !log) return;
    const done = () => {
      jumpPages.current = 0;
      useChat.setState({ jump: null });
    };
    if (log.messages.some((m) => m.id === jumpTarget)) {
      requestAnimationFrame(() => jumpTo(jumpTarget));
      return done();
    }
    if (!log.hasMore || jumpPages.current >= 20) return done();
    if (log.loadingOlder) return;
    jumpPages.current += 1;
    void loadOlder(channel.id);
  }, [jumpTarget, log, channel.id, jumpTo]);

  function startReply(message: Message) {
    setReplyTo({ id: message.id, author: message.author, content: message.content.slice(0, 200), deleted: false });
    setActiveId(null);
    composer.current?.focus();
  }

  function send(content: string, attachments: Attachment[]) {
    // `/jira NOV Başlık`: the "Jira işi aç" form instead of a message (docs/plans/2026-11-04-jira-design.md §4).
    const command = attachments.length === 0 && !threadRootId ? parseJiraCommand(content) : null;
    if (command) {
      useJiraCreate.getState().open({ channelId: channel.id, summary: command.summary, description: '', ...(command.projectKey ? { projectKey: command.projectKey } : {}) });
      return;
    }
    sendMessage(channel.id, content, replyTo, attachments, threadRootId);
    setReplyTo(null);
    requestAnimationFrame(toEnd);
  }

  const start = threadRootId ? root ? <ThreadStart channel={channel} me={me} root={root} /> : null : <ChannelStart channel={channel} />;

  function editLastOwn() {
    const last = log?.messages.findLast((m) => m.author?.id === me.id && !m.deletedAt);
    if (last) setEditingId(last.id);
  }

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col"
      onDragEnter={(e) => {
        if (!canAttach || !hasFiles(e)) return;
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => canAttach && hasFiles(e) && e.preventDefault()}
      onDragLeave={(e) => {
        if (!canAttach || !hasFiles(e)) return;
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setDragging(false);
      }}
      onDrop={(e) => {
        if (!canAttach || !hasFiles(e)) return;
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        addFiles.current([...e.dataTransfer.files]);
      }}
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-2 z-30 grid place-items-center rounded-lg border-2 border-dashed border-brand-400 bg-surface-1/90 text-center">
          <span className="flex flex-col items-center gap-2 font-heading text-lg font-bold">
            <Upload size={28} className="text-brand-400" aria-hidden="true" />
            Bırak, yükleyelim
            <span className="font-sans text-xs font-normal text-fg-3">Dosya başına en fazla 95 MB</span>
          </span>
        </div>
      )}
      <div
        ref={viewport}
        className="relative flex min-h-0 flex-1 flex-col"
        onWheel={() => (readerScrolled.current = true)}
        onTouchMove={() => (readerScrolled.current = true)}
      >
        {!log ? (
          <div className="grid flex-1 place-items-center p-6 text-center text-sm text-fg-3">
            {error ? (
              <div className="flex flex-col items-center gap-3">
                <p className="m-0">{error}</p>
                <button
                  type="button"
                  onClick={() => (setError(null), void loadNewest().catch((e: unknown) => setError(errorMessage(e))))}
                  className="nc-press h-9 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold text-fg-1"
                >
                  Tekrar dene
                </button>
              </div>
            ) : (
              'Mesajlar yükleniyor…'
            )}
          </div>
        ) : (
          <Virtuoso
            ref={list}
            className="flex-1"
            data={rows}
            firstItemIndex={log.firstIndex}
            initialTopMostItemIndex={{ index: Math.max(rows.length - 1, 0), align: 'end' }}
            alignToBottom
            // Instant, not smooth: a smooth scroll heads for where the bottom was and ends short of it
            // when the new message grows a moment later (markdown, link card, a failed-send line).
            followOutput={(bottom) => (bottom ? 'auto' : false)}
            atBottomStateChange={setAtBottom}
            // Rows grow after fonts load, a reaction lands or a status line changes; stay on the newest message.
            totalListHeightChanged={() => {
              if (stayDown()) requestAnimationFrame(toEnd);
            }}
            atBottomThreshold={80}
            startReached={loadEarlier}
            increaseViewportBy={{ top: 600, bottom: 200 }}
            computeItemKey={(_, row) => `${row.message.author?.id ?? '-'}:${row.message.clientId}`}
            context={{ channel, loading: log.loadingOlder, hasMore: log.hasMore, empty: rows.length === 0, start }}
            components={listParts}
            itemContent={(index, row) => {
              const position = index - log.firstIndex;
              const message = row.message;
              const key = message.id || row.pending!.clientId;
              const item = (
                <MessageRow
                  channel={channel}
                  me={me}
                  message={message}
                  pending={row.pending}
                  // A thread's first reply follows its first message (one "Bugün" line, not two).
                  previous={rows[position - 1]?.message ?? root ?? null}
                  firstUnread={message.id !== '' && message.id === firstUnreadId}
                  active={activeId === key}
                  editing={editingId === message.id && message.id !== ''}
                  highlighted={highlightId === message.id && message.id !== ''}
                  onActivate={() => setActiveId((id) => (id === key ? null : key))}
                  onReply={() => startReply(message)}
                  onEdit={() => (setEditingId(message.id), setActiveId(null))}
                  onEditDone={() => setEditingId(null)}
                  onDelete={() => (setDeleting(message), setActiveId(null))}
                  onJump={jumpTo}
                  onThread={!threadRootId && message.kind === 'user' && message.id ? () => openThread(channel.id, message.id) : undefined}
                />
              );
              // The channel's first message carries the welcome card (a thread's, its first message), so it sits right above it.
              return position === 0 && !log.hasMore ? (
                <>
                  {start}
                  {item}
                </>
              ) : (
                item
              );
            }}
          />
        )}
        {log && !atBottom && (
          <button
            type="button"
            onClick={() => list.current?.scrollToIndex({ index: 'LAST', behavior: 'smooth' })}
            className="nc-press absolute right-4 bottom-3 flex h-9 items-center gap-1.5 rounded-full border border-line-strong bg-surface-2 px-3.5 text-[13px] font-semibold shadow-lg"
          >
            <ArrowDown size={15} aria-hidden="true" />
            Son mesajlara git
          </button>
        )}
      </div>

      {!threadRootId && <TypingLine channelId={channel.id} />}
      {canWrite ? (
        <Composer
          ref={composer}
          channel={channel}
          me={me}
          inThread={!!threadRootId}
          addFiles={addFiles}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onSend={send}
          onEditLast={editLastOwn}
        />
      ) : (
        <p className="m-0 mx-3 mb-3 rounded-lg border border-line bg-surface-2 px-4 py-3 text-center text-sm text-fg-3 md:mx-5 md:mb-4">{channel.kind === 'jira' ? 'Buraya Jira yazar; işlemleri kartlardan yapabilirsin.' : 'Bu kanalda yalnızca okuyabilirsin.'}</p>
      )}
      {deleting && <DeleteDialog message={deleting} onClose={() => setDeleting(null)} />}
    </div>
  );
}

type ListContext = { channel: Channel; loading: boolean; hasMore: boolean; empty: boolean; start: React.ReactNode };

/** Defined once: Virtuoso remounts parts whose component identity changes. */
const listParts: Components<Row, ListContext> = {
  Header: ({ context }) =>
    context.empty ? (
      context.start
    ) : context.hasMore ? (
      <p className="m-0 h-10 py-3 text-center text-xs text-fg-3">{context.loading ? 'Eski mesajlar yükleniyor…' : ''}</p>
    ) : null,
  Footer: () => <div className="h-2" />,
};

/** A thread's first message on top of its replies, and how many there are. */
function ThreadStart({ channel, me, root }: { channel: Channel; me: Me; root: Message }) {
  const noop = () => {};
  return (
    <div className="pt-2">
      <MessageRow channel={channel} me={me} message={root} previous={null} firstUnread={false} active={false} editing={false} highlighted={false} onActivate={noop} onReply={noop} onEdit={noop} onEditDone={noop} onDelete={noop} onJump={noop} />
      <div role="separator" className="flex items-center gap-3 px-4 py-2 text-xs font-semibold text-fg-3 md:px-5">
        {root.thread ? `${root.thread.count} yanıt` : 'Henüz yanıt yok'}
        <span className="h-px flex-1 bg-line" />
      </div>
    </div>
  );
}

/** "Konu dalı": a thread next to (or, on narrow screens, instead of) its channel's chat. */
function ThreadPanel({ channel, me, rootId }: { channel: Channel; me: Me; rootId: string }) {
  const following = useChat((s) => s.threads[rootId]?.following);
  return (
    <section aria-label="Konu dalı" className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex h-14 flex-none items-center gap-2 border-b border-line px-3 md:px-4">
        <MessagesSquare size={18} className="flex-none text-fg-2" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h2 className="m-0 truncate font-heading text-[15px] font-bold">Konu dalı</h2>
          <p className="m-0 truncate text-xs text-fg-3">{channel.kind !== 'text' ? titleOf(channel) : `#${channel.name}`}</p>
        </div>
        {following !== undefined && (
          <button
            type="button"
            aria-pressed={following}
            onClick={() => void followThread(rootId, !following).catch(() => {})}
            className="nc-press h-8 flex-none rounded-md border border-line-strong bg-surface-2 px-3 text-[13px] font-semibold text-fg-1"
          >
            {following ? 'Takibi bırak' : 'Takip et'}
          </button>
        )}
        <button type="button" aria-label="Dalı kapat" onClick={closeThreadPanel} className="nc-press grid size-9 flex-none place-items-center rounded-md border-0 bg-transparent text-fg-2 hover:bg-surface-2">
          <X size={18} />
        </button>
      </div>
      <Chat key={rootId} channel={channel} me={me} threadRootId={rootId} />
    </section>
  );
}

function ChannelStart({ channel }: { channel: Channel }) {
  return (
    <div className="px-4 pt-8 pb-4 md:px-5">
      {channel.kind === 'dm' && channel.peer ? (
        <Avatar id={channel.peer.id} name={channel.peer.name} size={56} large />
      ) : channel.logoUrl ? (
        <ChannelIcon channel={channel} size={56} />
      ) : (
        <span className="grid size-14 place-items-center rounded-[18px] bg-surface-2 text-fg-2">
          <ChannelIcon channel={channel} size={26} />
        </span>
      )}
      <h2 className="mt-3 mb-1 font-heading text-xl font-bold">{channel.kind !== 'text' ? titleOf(channel) : `#${channel.name}`}</h2>
      <p className="m-0 text-sm text-fg-3">
        {channel.kind === 'jira'
          ? 'Jira’da seni ilgilendirenler burada: sana atanan işler, senden bahsedilen yorumlar, açtığın ya da izlediğin işlerdeki yorumlar ve durum değişiklikleri.'
          : channel.kind === 'dm' ? `${titleOf(channel)} ile mesajlaşmanın başlangıcı.` : `Kanalın başlangıcı.${channel.topic ? ` ${channel.topic}` : ''}`}
      </p>
    </div>
  );
}

function ChannelHeader({ channel, me, chatToggle, canPop }: { channel: Channel; me: Me; chatToggle: ChatToggle | null; canPop: boolean }) {
  const [menu, setMenu] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const menuPanel = useRef<HTMLDivElement>(null);
  useDismiss(menu, () => setMenu(false), menuPanel, menuButton);
  const [bell, setBell] = useState(false);
  const bellButton = useRef<HTMLButtonElement>(null);
  const bellPanel = useRef<HTMLDivElement>(null);
  useDismiss(bell, () => setBell(false), bellPanel, bellButton);
  const [open, setOpen] = useState<'edit' | 'members' | 'archive' | 'pins' | 'leave' | 'jira' | null>(null);
  const jiraAvailable = useJira().data?.available ?? false;
  // A dialog that hands over to another (leave → members/archive) must not be undone by its own close event.
  const closeIf = (which: NonNullable<typeof open>) => () => setOpen((current) => (current === which ? null : current));
  const peerPresence = useLive((s) => (channel.peer ? (s.presence[channel.peer.id] ?? 'offline') : 'offline'));
  const isDm = channel.kind !== 'text';
  const queries = useQueryClient();
  async function setLevel(level: Channel['notifyLevel']) {
    setMenu(false);
    setBell(false);
    await client.api.setNotifyLevel(channel.id, level).catch(() => {});
    await queries.invalidateQueries({ queryKey: ['channels'] });
  }
  const item = (icon: React.ReactNode, label: string, dialog: NonNullable<typeof open>, danger: boolean | 'warn' = false, className = '') => (
    <button
      type="button"
      role="menuitem"
      onClick={() => (setMenu(false), setOpen(dialog))}
      className={`flex w-full items-center gap-2.5 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] hover:bg-surface-3 ${danger === 'warn' ? 'text-yellow-500' : danger ? 'text-[#EE9191]' : 'text-fg-1'} ${className}`}
    >
      {icon}
      {label}
    </button>
  );
  const iconButton = 'nc-press grid size-[34px] flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2';

  return (
    <header className="relative flex min-h-14 items-center gap-2 border-b md:gap-3 border-line pt-[max(8px,env(safe-area-inset-top))] pr-3 pb-2 pl-2 md:pr-4 md:pl-5">
      <button
        type="button"
        aria-label="Listeye dön"
        onClick={goBack}
        className="grid size-10 flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2 md:hidden"
      >
        <ChevronLeft size={24} />
      </button>
      {isDm && channel.peer ? (
        <Avatar id={channel.peer.id} name={channel.peer.name} size={28} presence={peerPresence} />
      ) : (
        <ChannelIcon channel={channel} size={channel.logoUrl ? 26 : 20} className="text-fg-2" />
      )}
      <h1 className="m-0 min-w-0 truncate font-heading text-lg font-bold">{titleOf(channel)}</h1>
      {(isDm || channel.topic) && (
        <>
          <span className="h-5 w-px flex-none bg-line-strong max-sm:hidden" aria-hidden="true" />
          <p className="m-0 min-w-0 flex-1 truncate text-[13px] text-fg-3 max-sm:hidden">{isDm ? presenceText(peerPresence) : channel.topic}</p>
        </>
      )}
      <span className="flex-1 sm:hidden" />
      {!(isDm || channel.topic) && <span className="flex-1 max-sm:hidden" />}
      {!isDm && <CallButton channel={channel} me={me} />}
      {canPop && (
        <button
          type="button"
          aria-label="Ayrı pencerede aç"
          title="Görüşmeyi her şeyin üstünde duran ayrı bir pencerede aç"
          onClick={() => void usePopout.getState().open()}
          className={`${iconButton} max-md:hidden`}
        >
          <PictureInPicture2 size={18} />
        </button>
      )}
      {chatToggle &&
        (chatToggle.wide ? (
          <button
            type="button"
            aria-pressed={chatToggle.open}
            aria-label={chatToggle.open ? 'Sohbeti gizle' : 'Sohbeti göster'}
            title={chatToggle.open ? 'Sohbeti gizle' : 'Sohbeti göster'}
            onClick={chatToggle.onClick}
            className={`${iconButton} relative ${chatToggle.open ? 'bg-surface-2 text-fg-1' : ''}`}
          >
            <MessageSquare size={18} />
            {chatToggle.unread && <span className="absolute top-1 right-1 size-2 rounded-full bg-brand-400" aria-hidden="true" />}
          </button>
        ) : (
          // Phones switch between the two, so the button says where it goes.
          <button
            type="button"
            aria-label={chatToggle.open ? 'Görüşmeye dön' : 'Sohbet'}
            onClick={chatToggle.onClick}
            className="nc-press relative grid size-[34px] flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2 hover:text-fg-1"
          >
            {chatToggle.open ? <Phone size={17} aria-hidden="true" /> : <MessageSquare size={17} aria-hidden="true" />}
            {chatToggle.unread && <span className="absolute -top-1 -right-1 size-2.5 rounded-full border-2 border-surface-1 bg-brand-400" aria-hidden="true" />}
          </button>
        ))}
      <button type="button" aria-label="Sabitlenen mesajlar" title="Sabitlenen mesajlar" onClick={() => setOpen('pins')} className={`${iconButton} max-md:hidden`}>
        <Pin size={18} />
      </button>
      <button
        ref={bellButton}
        type="button"
        aria-label={`Bildirimler: ${{ all: 'Tüm mesajlar', mentions: 'Sadece bahsetmeler', mute: 'Sessiz' }[channel.notifyLevel]}`}
        aria-expanded={bell}
        onClick={() => setBell((b) => !b)}
        className={iconButton}
      >
        {channel.notifyLevel === 'mute' ? <BellOff size={18} /> : <Bell size={18} />}
      </button>
      {bell && (
        <div ref={bellPanel} role="menu" aria-label="Bildirimler" className="absolute top-12 right-14 z-20 w-56 animate-enter rounded-md border border-line-strong bg-surface-2 p-1 shadow-2xl">
            <p className="m-0 px-2.5 pt-1 pb-0.5 text-xs font-semibold text-fg-3">Bildirimler</p>
            {(
              [
                ['all', 'Tüm mesajlar'],
                ['mentions', 'Sadece bahsetmeler'],
                ['mute', 'Sessiz'],
              ] as const
            ).map(([level, label]) => (
              <button
                key={level}
                type="button"
                role="menuitemradio"
                aria-checked={channel.notifyLevel === level}
                onClick={() => void setLevel(level)}
                className="flex w-full items-center gap-2.5 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] text-fg-1 hover:bg-surface-3"
              >
                <span className="flex-1">{label}</span>
                {channel.notifyLevel === level && <Check size={15} className="text-green-400" aria-hidden="true" />}
              </button>
            ))}
          </div>
      )}
      <button
        ref={menuButton}
        type="button"
        aria-label="Kanal ayarları"
        aria-expanded={menu}
        onClick={() => setMenu((m) => !m)}
        className={iconButton}
      >
        <MoreHorizontal size={18} />
      </button>
      {menu && (
        <div ref={menuPanel} role="menu" className="absolute top-12 right-4 z-20 w-52 animate-enter rounded-md border border-line-strong bg-surface-2 p-1 shadow-2xl">
          {item(<Pin size={15} />, 'Sabitlenen mesajlar', 'pins', false, 'md:hidden')}
          {channel.permissions.includes('edit_channel') && item(<Pencil size={15} />, 'Kanalı düzenle', 'edit')}
          {!isDm && jiraAvailable && channel.permissions.includes('edit_channel') && item(<Rss size={15} />, 'Jira akışı', 'jira')}
          {!isDm && item(<Users size={15} />, channel.permissions.includes('edit_roles') ? 'Üyeler ve roller' : 'Üyeler', 'members')}
          {!isDm && item(<LogOut size={15} />, 'Kanaldan ayrıl', 'leave', true)}
          {!isDm && channel.permissions.includes('delete_channel') && item(<Archive size={15} />, 'Kanalı arşivle', 'archive', 'warn')}
        </div>
      )}
      {open === 'edit' && <EditRoomDialog channel={channel} onClose={() => setOpen(null)} />}
      {open === 'members' && <PeopleDialog channel={channel} me={me} onClose={closeIf('members')} />}
      {open === 'archive' && <ArchiveDialog channel={channel} onClose={closeIf('archive')} />}
      {open === 'leave' && <LeaveDialog channel={channel} me={me} onClose={closeIf('leave')} onPeople={() => setOpen('members')} onArchive={() => setOpen('archive')} />}
      {open === 'pins' && <PinsDialog channel={channel} onClose={() => setOpen(null)} />}
      {open === 'jira' && <JiraFeedDialog channel={channel} onClose={() => setOpen(null)} />}
    </header>
  );
}

/** "Ayşe yazıyor…" under the list; the line keeps its height so nothing jumps. */
function TypingLine({ channelId }: { channelId: string }) {
  const typing = useChat((s) => s.typing[channelId]);
  const people = useDirectory();
  const [now, setNow] = useState(() => Date.now());
  const active = Object.entries(typing ?? {}).filter(([, until]) => until > now).map(([userId]) => userId);

  useEffect(() => {
    if (active.length === 0) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active.length]);
  useEffect(() => setNow(Date.now()), [typing]);

  const names = active.map((id) => firstName(people.find((u) => u.id === id)?.name ?? 'Biri'));
  const text = names.length === 0 ? '' : names.length === 1 ? names[0]! : names.length === 2 ? `${names[0]} ve ${names[1]}` : `${names.length} kişi`;

  return (
    <div aria-live="polite" className="flex h-6 flex-none items-center gap-2 px-4 text-xs text-fg-3 md:px-6">
      {text && (
        <>
          <span className="flex gap-[3px]" aria-hidden="true">
            {[0, 0.2, 0.4].map((delay) => (
              <span key={delay} className="size-[5px] animate-typing rounded-full bg-fg-2" style={{ animationDelay: `${delay}s` }} />
            ))}
          </span>
          <span>
            <span className="font-semibold text-fg-2">{text}</span> yazıyor…
          </span>
        </>
      )}
    </div>
  );
}

interface ComposerProps {
  ref: React.Ref<HTMLTextAreaElement>;
  channel: Channel;
  me: Me;
  /** Filled in by the composer so drops on the whole chat land in its upload list. */
  addFiles: React.RefObject<(files: File[]) => void>;
  replyTo: ReplyPreview | null;
  onCancelReply: () => void;
  onSend: (content: string, attachments: Attachment[]) => void;
  onEditLast: () => void;
  /** In a thread: says so, and others do not see "yazıyor…" in the channel. */
  inThread?: boolean;
}

/** Enter sends on computers; phones add a line with Enter and send with the button. */
type Suggestion = { id: string; name: string };

const SEND_KEY = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘ Enter' : 'Ctrl + Enter';
/** Odd number of ``` before the caret: we are inside a code block. */
const insideCode = (textBefore: string) => (textBefore.match(/```/g)?.length ?? 0) % 2 === 1;

interface Upload {
  key: string;
  file: File;
  /** Local thumbnail for images. */
  preview: string | null;
  progress: number;
  attachment: Attachment | null;
  error: string | null;
  abort: AbortController;
}

function Composer({ ref, channel, me, addFiles, replyTo, onCancelReply, onSend, onEditLast, inThread = false }: ComposerProps) {
  const canAttach = channel.permissions.includes('files');
  const [uploads, setUploads] = useState<Upload[]>([]);
  const picker = useRef<HTMLInputElement>(null);
  const update = (key: string, patch: Partial<Upload>) => setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...patch } : u)));

  function startUploads(files: File[]) {
    const room = MAX_ATTACHMENTS - uploads.length;
    for (const file of files.slice(0, Math.max(0, room))) {
      const upload: Upload = {
        key: crypto.randomUUID(),
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        progress: 0,
        attachment: null,
        error: file.size > MAX_UPLOAD_BYTES ? 'En fazla 95 MB' : null,
        abort: new AbortController(),
      };
      setUploads((list) => [...list, upload]);
      if (upload.error) continue;
      uploadFile(channel.id, file, (progress) => update(upload.key, { progress }), upload.abort.signal)
        .then((attachment) => update(upload.key, { attachment, progress: 1 }))
        .catch((e: unknown) => {
          if ((e as Error).name !== 'AbortError') update(upload.key, { error: errorMessage(e) });
        });
    }
  }
  useEffect(() => {
    addFiles.current = startUploads;
  });

  function removeUpload(key: string) {
    setUploads((list) => {
      const gone = list.find((u) => u.key === key);
      gone?.abort.abort();
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return list.filter((u) => u.key !== key);
    });
  }
  const uploading = uploads.some((u) => !u.attachment && !u.error);
  const ready = uploads.flatMap((u) => (u.attachment ? [u.attachment] : []));

  const [text, setText] = useState(() => drafts.get(channel.id) ?? '');
  const input = useRef<HTMLTextAreaElement | null>(null);
  const hasKeyboard = useHasKeyboard();
  const people = useDirectory();
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [picked, setPicked] = useState(0);

  // "@" opens a short list of people (and @herkes, when the reader's roles allow it) matching what follows.
  const suggestions = useMemo<Suggestion[]>(() => {
    if (!mention) return [];
    const query = mention.query.toLocaleLowerCase('tr');
    const matches = people.filter((p) => p.id !== me.id && p.name.toLocaleLowerCase('tr').split(' ').some((word, i, words) => words.slice(i).join(' ').startsWith(query)));
    const everyone = channel.permissions.includes('mention_everyone') && channel.kind !== 'dm' && 'herkes'.startsWith(query) ? [{ id: 'everyone', name: 'herkes' }] : [];
    return [...everyone, ...matches].slice(0, 6);
  }, [mention, people, me, channel]);

  const [inCode, setInCode] = useState(false);
  // Where the caret goes after a programmatic edit; applied as soon as React updates the field,
  // so nothing typed right after a click lands in the old spot.
  const nextCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = input.current;
    if (!el || nextCaret.current === null) return;
    el.focus();
    el.setSelectionRange(nextCaret.current, nextCaret.current);
    nextCaret.current = null;
  }, [text]);

  function track(el: HTMLTextAreaElement) {
    setMention(mentionQuery(el.value, el.selectionStart));
    setPicked(0);
    setInCode(insideCode(el.value.slice(0, el.selectionStart)));
  }

  /** Wraps the selection (or nothing) in a code block, for keyboards where ``` is hard to type. */
  function insertCodeBlock() {
    const el = input.current;
    if (!el) return;
    const { selectionStart: from, selectionEnd: to } = el;
    const before = text.slice(0, from);
    const selected = text.slice(from, to);
    const after = text.slice(to);
    const open = `${before && !before.endsWith('\n') ? '\n' : ''}\`\`\`\n`;
    const close = `\n\`\`\`${after.startsWith('\n') ? '' : '\n'}`;
    nextCaret.current = before.length + open.length + selected.length;
    setText(before + open + selected + close + after);
    setInCode(true);
  }

  // "Bahset" in a profile card writes here, where the caret is (or at the end).
  useEffect(() => {
    useComposer.setState({ channelId: channel.id });
    return () => useComposer.setState({ channelId: null, insert: null });
  }, [channel.id]);
  const requested = useComposer((s) => s.insert);
  useEffect(() => {
    if (!requested) return;
    const el = input.current;
    const at = el && document.activeElement === el ? el.selectionStart : text.length;
    const before = text.slice(0, at);
    const glue = before && !/\s$/.test(before) ? ' ' : '';
    nextCaret.current = before.length + glue.length + requested.length;
    setText(before + glue + requested + text.slice(at));
    useComposer.setState({ insert: null });
    // Only a new request matters; the text is read as it is at that moment.
  }, [requested]);

  function choose(suggestion: Suggestion) {
    const el = input.current;
    if (!el || !mention) return;
    const insert = `@${suggestion.name} `;
    const next = text.slice(0, mention.start) + insert + text.slice(el.selectionStart);
    nextCaret.current = mention.start + insert.length;
    setText(next);
    setMention(null);
  }

  useEffect(() => {
    drafts.set(channel.id, text);
    const el = input.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text, channel.id]);

  useEffect(() => {
    if (hasKeyboard) input.current?.focus();
  }, [channel.id, hasKeyboard]);

  function submit() {
    const content = text.trim();
    if (uploading || (!content && ready.length === 0)) return;
    onSend(toWire(content, people), ready);
    for (const u of uploads) if (u.preview) URL.revokeObjectURL(u.preview);
    setUploads([]);
    setText('');
    setMention(null);
    setInCode(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.nativeEvent.isComposing) return;
    if (suggestions.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setPicked((i) => (i + step + suggestions.length) % suggestions.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        choose(suggestions[picked] ?? suggestions[0]!);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setMention(null);
        return;
      }
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    } else if (event.key === 'Enter' && !event.shiftKey && hasKeyboard) {
      // Inside a code block Enter adds a line, like Discord.
      if (insideCode(text.slice(0, event.currentTarget.selectionStart))) return;
      event.preventDefault();
      submit();
    } else if (event.key === 'Escape' && replyTo) {
      event.preventDefault();
      onCancelReply();
    } else if (event.key === 'ArrowUp' && text === '') {
      event.preventDefault();
      onEditLast();
    }
  }

  const placeholder = inThread ? 'Dala yanıt yaz' : channel.kind === 'dm' ? `${titleOf(channel)} kişisine yaz` : `#${channel.name} kanalına yaz`;

  return (
    <div className="flex-none px-3 pb-[max(12px,env(safe-area-inset-bottom))] md:px-5 md:pb-4">
      {replyTo && (
        <div className="mb-1.5 flex items-center gap-2 rounded-md bg-surface-2 px-3 py-1.5 text-[13px] text-fg-3">
          <span className="min-w-0 flex-1 truncate">
            <span className="font-semibold text-fg-2">{replyTo.author?.name ?? 'Silinmiş kullanıcı'}</span> kişisine yanıt: {plainText(replyTo.content, people)}
          </span>
          <button type="button" aria-label="Yanıtı iptal et" onClick={onCancelReply} className="grid size-6 place-items-center rounded-xs border-0 bg-transparent text-fg-2 hover:bg-surface-3">
            <X size={14} />
          </button>
        </div>
      )}
      {suggestions.length > 0 && (
        <ul role="listbox" aria-label="Bahsedilecek kişi" className="m-0 mb-1.5 flex list-none flex-col rounded-md border border-line-strong bg-surface-2 p-1 shadow-lg">
          {suggestions.map((suggestion, i) => (
            <li key={suggestion.id} role="option" aria-selected={i === picked}>
              <button
                type="button"
                // Keep the textarea focused (and its caret) while picking with the mouse.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(suggestion)}
                className={`flex w-full items-center gap-2.5 rounded-sm border-0 px-2 py-1.5 text-left text-sm text-fg-1 ${i === picked ? 'bg-surface-3' : 'bg-transparent hover:bg-surface-3'}`}
              >
                {suggestion.id === 'everyone' ? (
                  <span className="grid size-6 place-items-center rounded-[7px] bg-brand-500/25 text-xs font-bold text-brand-300">@</span>
                ) : (
                  <Avatar id={suggestion.id} name={suggestion.name} size={24} />
                )}
                <span className="min-w-0 flex-1 truncate">{suggestion.name}</span>
                {suggestion.id === 'everyone' && <span className="text-xs text-fg-3">Kanaldaki herkese bildirir</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {uploads.length > 0 && (
        <ul aria-label="Eklenecek dosyalar" className="m-0 mb-1.5 flex list-none gap-2 overflow-x-auto p-0 pt-1.5">
          {uploads.map((u) => (
            <li key={u.key} className="relative flex w-44 flex-none items-center gap-2 rounded-md border border-line bg-surface-2 p-1.5">
              {u.preview ? (
                <img src={u.preview} alt="" className="size-10 flex-none rounded-sm object-cover" />
              ) : (
                <span className="grid size-10 flex-none place-items-center rounded-sm bg-surface-3 text-fg-2">
                  <FileIcon size={18} aria-hidden="true" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-semibold">{u.file.name}</span>
                {u.error ? (
                  <span className="block truncate text-[11px] text-[#EE9191]">{u.error}</span>
                ) : u.attachment ? (
                  <span className="block text-[11px] text-fg-3">{sizeText(u.file.size)}</span>
                ) : (
                  <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-valuenow={Math.round(u.progress * 100)} aria-label="Yükleniyor">
                    <span className="block h-full bg-brand-400 transition-[width]" style={{ width: `${Math.round(u.progress * 100)}%` }} />
                  </span>
                )}
              </span>
              <button
                type="button"
                aria-label={`${u.file.name} dosyasını kaldır`}
                onClick={() => removeUpload(u.key)}
                className="absolute -top-1.5 -right-1.5 grid size-5 place-items-center rounded-full border border-line-strong bg-surface-3 text-fg-2 hover:text-fg-1"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-1.5 rounded-lg border border-line-strong bg-surface-2 p-2 focus-within:border-fg-3">
        {canAttach && (
          <button
            type="button"
            aria-label="Dosya ekle"
            title="Dosya ekle (sürükleyip bırakabilir ya da yapıştırabilirsin)"
            onClick={() => picker.current?.click()}
            className="nc-press grid size-[38px] flex-none place-items-center rounded-[11px] border-0 bg-surface-3 text-fg-2 hover:text-fg-1"
          >
            <Paperclip size={18} />
          </button>
        )}
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            startUploads([...(e.target.files ?? [])]);
            e.target.value = '';
          }}
        />
        <label htmlFor="nc-composer" className="sr-only">
          Mesaj
        </label>
        <textarea
          id="nc-composer"
          ref={(el) => {
            input.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }}
          rows={1}
          value={text}
          maxLength={4000}
          placeholder={placeholder}
          enterKeyHint={hasKeyboard ? 'send' : 'enter'}
          aria-autocomplete="list"
          aria-expanded={suggestions.length > 0}
          onChange={(e) => {
            setText(e.target.value);
            track(e.target);
            if (e.target.value.trim() && !inThread) notifyTyping(channel.id);
          }}
          onSelect={(e) => track(e.currentTarget)}
          onBlur={() => setMention(null)}
          onPaste={(e) => {
            const files = [...e.clipboardData.files];
            if (files.length === 0 || !canAttach) return;
            e.preventDefault();
            startUploads(files);
          }}
          onKeyDown={onKeyDown}
          className="max-h-[200px] min-h-[38px] min-w-0 flex-1 resize-none border-0 bg-transparent px-1.5 py-2 text-base leading-[22px] text-fg-1 outline-none placeholder:text-fg-3 md:text-[15px]"
        />
        <button
          type="button"
          aria-label="Kod bloğu ekle"
          title="Kod bloğu ekle"
          onMouseDown={(e) => e.preventDefault()}
          onClick={insertCodeBlock}
          className="nc-press grid size-[38px] flex-none place-items-center rounded-[11px] border-0 bg-transparent text-fg-2 hover:bg-surface-3 hover:text-fg-1"
        >
          <Code size={18} />
        </button>
        <button
          type="button"
          aria-label="Gönder"
          disabled={uploading || (!text.trim() && ready.length === 0)}
          onClick={() => {
            submit();
            input.current?.focus();
          }}
          className="nc-press nc-cta grid size-[38px] flex-none place-items-center rounded-[11px] border-0 bg-brand-500 text-white disabled:bg-surface-3 disabled:text-fg-3 disabled:shadow-none"
        >
          <SendHorizontal size={18} />
        </button>
      </div>
      {hasKeyboard && (
        <p className="m-0 px-1.5 pt-1.5 text-xs text-fg-3">
          {inCode ? (
            <>
              Kod bloğundasın: <span className="font-semibold text-fg-2">Enter</span> yeni satır ekler ·{' '}
              <span className="font-semibold text-fg-2">{SEND_KEY}</span> gönderir
            </>
          ) : (
            <>
              <span className="font-semibold text-fg-2">Enter</span> gönderir · <span className="font-semibold text-fg-2">Shift + Enter</span> yeni satır ·{' '}
              <span className="font-semibold text-fg-2">↑</span> son mesajını düzenler · kod için <span className="font-semibold text-fg-2">```</span>
            </>
          )}
        </p>
      )}
    </div>
  );
}

function DeleteDialog({ message, onClose }: { message: Message; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const people = useDirectory();
  return (
    <Modal title="Mesaj silinsin mi?" onClose={onClose}>
      {(close) => (
        <div className="flex flex-col gap-4">
          <blockquote className="m-0 max-h-40 overflow-y-auto rounded-md border border-line bg-surface-2 px-3 py-2 text-sm break-words whitespace-pre-wrap text-fg-2">
            {plainText(message.content, people)}
          </blockquote>
          <p className="m-0 text-sm text-fg-3">Mesajın yerinde “Bu mesaj silindi” yazar. Bu geri alınamaz.</p>
          {error && (
            <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={close} className="nc-press h-10 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold">
              Vazgeç
            </button>
            <button
              type="button"
              onClick={() =>
                void deleteMessage(message)
                  .then(close)
                  .catch((e: unknown) => setError(errorMessage(e)))
              }
              className="nc-press h-10 rounded-md border-0 bg-red-400 px-4 text-sm font-bold text-white"
            >
              Sil
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function PinsDialog({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const pins = useQuery({ queryKey: ['pins', channel.id], queryFn: () => client.api.pins(channel.id) });
  const people = useDirectory();
  return (
    <Modal title="Sabitlenen mesajlar" onClose={onClose}>
      {() => (
        <div className="flex max-h-[60dvh] flex-col gap-2 overflow-y-auto">
          {pins.isPending && <p className="m-0 text-sm text-fg-3">Yükleniyor…</p>}
          {pins.data?.length === 0 && <p className="m-0 text-sm leading-normal text-fg-3">Henüz sabitlenen mesaj yok. Bir mesajın üstüne gelip raptiyeye bas.</p>}
          {pins.data?.map((message) => (
            <article key={message.id} className="rounded-md border border-line bg-surface-2 px-3 py-2">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="font-bold">{message.author?.name ?? 'Silinmiş kullanıcı'}</span>
                <span className="text-xs text-fg-3">{clock(message.createdAt)}</span>
                <span className="flex-1" />
                <button
                  type="button"
                  onClick={() => void pinMessage(message.id, false).then(() => pins.refetch())}
                  className="border-0 bg-transparent p-0 text-xs font-semibold text-fg-3 hover:text-fg-1"
                >
                  Kaldır
                </button>
              </div>
              <p className="m-0 mt-1 text-sm break-words whitespace-pre-wrap text-fg-body">{plainText(message.content, people)}</p>
            </article>
          ))}
        </div>
      )}
    </Modal>
  );
}
